import React, { useEffect, useState } from 'react';
import PricingSnapshotFields, {
  emptyPricingSnapshot,
  normalizePricingSnapshot,
} from './PricingSnapshotFields';
import CustomerDialNumberPicker from '../../components/CustomerDialNumberPicker/CustomerDialNumberPicker';
import {
  buildCustomerDialOptions,
  fetchCustomerExtraPhones,
} from '../../utils/customerVoiceCall';
import s from './Leads.module.scss';

const FOLLOW_UP_PRESETS = [
  { label: 'Tomorrow', days: 1 },
  { label: '+3 days', days: 3 },
  { label: '+7 days', days: 7 },
  { label: '2 weeks', days: 14 },
];

const toLocalDateTimeInput = (dt = new Date()) => {
  const d = new Date(dt);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const getFutureDateTime = (daysFromNow) => {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  d.setHours(9, 0, 0, 0);
  return toLocalDateTimeInput(d);
};

const LeadStatusModal = ({ variant, lead, onConfirm, onClose }) => {
  const [customDate, setCustomDate] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [followUpNote, setFollowUpNote] = useState('');
  const [dropReason, setDropReason] = useState('');
  const [pricing, setPricing] = useState(() => emptyPricingSnapshot(lead));
  const [dialOptions, setDialOptions] = useState([]);
  const [dialPhone, setDialPhone] = useState('');

  useEffect(() => {
    if (variant !== 'voice_call' || !lead) return undefined;
    const primary = lead.customer_phone || lead.phone_number || '';
    let cancelled = false;
    const apply = (extras) => {
      if (cancelled) return;
      const options = buildCustomerDialOptions(primary, extras);
      setDialOptions(options);
      setDialPhone(options[0]?.phone || String(primary).replace(/\D/g, ''));
    };
    apply([]);
    const customerId = lead.customer_id;
    if (customerId) {
      fetchCustomerExtraPhones(customerId).then(apply);
    }
    return () => {
      cancelled = true;
    };
  }, [variant, lead]);

  if (!lead) return null;

  const handlePresetClick = (days) => {
    setSelectedPreset(days);
    setCustomDate('');
  };

  const handleCustomChange = (e) => {
    setCustomDate(e.target.value);
    setSelectedPreset(null);
  };

  const handleConfirm = () => {
    if (variant === 'follow_up') {
      let followUpAt;
      if (selectedPreset !== null) {
        followUpAt = getFutureDateTime(selectedPreset);
      } else if (customDate) {
        followUpAt = customDate;
      } else {
        return;
      }
      const d = new Date(followUpAt);
      const now = new Date();
      if (d.getTime() < now.getTime()) return;
      const note = followUpNote.trim();
      if (!note) return;
      onConfirm(followUpAt, note, normalizePricingSnapshot(pricing));
    } else if (variant === 'drop') {
      if (!dropReason.trim()) return;
      onConfirm(dropReason.trim(), normalizePricingSnapshot(pricing));
    } else if (variant === 'pricing_convert' || variant === 'pricing') {
      onConfirm(normalizePricingSnapshot(pricing));
    } else {
      onConfirm();
    }
  };

  const canConfirmFollowUp = () => {
    if (selectedPreset === null && !customDate) return false;
    if (!followUpNote.trim()) return false;
    const d = new Date(selectedPreset !== null ? getFutureDateTime(selectedPreset) : customDate);
    const now = new Date();
    return d.getTime() >= now.getTime();
  };
  const phoneDisplay = lead.customer_phone || lead.phone_number || '';
  const nameDisplay = lead.customer_name || lead.business_name || 'Unknown';
  const hasExistingPricing = [
    lead.customer_expectation_rate,
    lead.offered_rate,
    lead.best_transporter_rate,
    lead.best_transporter_name,
  ].some((value) => value != null && value !== '');

  return (
    <div className={s.modalBackdrop} onClick={onClose}>
      <div className={s.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        {variant === 'follow_up' ? (
          <>
            <div className={s.modalHeader}>
              <i className="fa fa-clock-o" />
              Schedule Follow-up
            </div>
            <div className={s.modalBody}>
              <div className={s.modalLeadSummary}>
                <span className={s.modalLeadName}>{nameDisplay}</span>
                <span className={s.modalLeadPhone}>{phoneDisplay}</span>
              </div>
              <p className={s.modalPrompt}>When should this lead surface back for action?</p>
              <div className={s.followUpPresets}>
                {FOLLOW_UP_PRESETS.map((p) => (
                  <button
                    key={p.days}
                    type="button"
                    className={`${s.followUpPresetBtn} ${selectedPreset === p.days ? s.active : ''}`}
                    onClick={() => handlePresetClick(p.days)}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  className={`${s.followUpPresetBtn} ${customDate ? s.active : ''}`}
                  onClick={() => { setSelectedPreset(null); setCustomDate(getFutureDateTime(0)); }}
                >
                  Custom
                </button>
              </div>
              {customDate !== '' && (
                <input
                  type="datetime-local"
                  className={s.followUpDateInput}
                  value={customDate}
                  onChange={handleCustomChange}
                  min={toLocalDateTimeInput()}
                />
              )}
              <div style={{ marginTop: '12px' }}>
                <label className={s.formLabel}>Note <span style={{ color: '#ef4444' }}>*</span></label>
                <textarea
                  className={s.followUpNoteInput}
                  rows={3}
                  placeholder="Add a note for this follow-up..."
                  value={followUpNote}
                  onChange={(e) => setFollowUpNote(e.target.value)}
                />
              </div>
              <PricingSnapshotFields value={pricing} onChange={setPricing} compact />
            </div>
            <div className={s.modalActions}>
              <button type="button" className={s.modalCancelBtn} onClick={onClose}>Cancel</button>
              <button
                type="button"
                className={s.modalConfirmBtn}
                onClick={handleConfirm}
                disabled={!canConfirmFollowUp()}
              >
                <i className="fa fa-clock-o" /> Schedule
              </button>
            </div>
          </>
        ) : variant === 'drop' ? (
          <>
            <div className={s.modalHeader}>
              <i className="fa fa-trash" style={{ color: '#ef4444' }} />
              Drop Lead
            </div>
            <div className={s.modalBody}>
              <div className={s.modalLeadSummary}>
                <span className={s.modalLeadName}>{nameDisplay}</span>
                <span className={s.modalLeadPhone}>{phoneDisplay}</span>
              </div>
              <p className={s.modalPrompt}>
                This lead will be moved to <strong>Dropped</strong>. You can reset it to New later if needed.
              </p>
              <div style={{ marginTop: '12px' }}>
                <label className={s.formLabel}>Reason for dropping <span style={{ color: '#ef4444' }}>*</span></label>
                <textarea
                  className={s.followUpNoteInput}
                  rows={3}
                  placeholder="Why is this lead being dropped?"
                  value={dropReason}
                  onChange={(e) => setDropReason(e.target.value)}
                  autoFocus
                />
              </div>
              <PricingSnapshotFields value={pricing} onChange={setPricing} compact />
            </div>
            <div className={s.modalActions}>
              <button type="button" className={s.modalCancelBtn} onClick={onClose}>Cancel</button>
              <button
                type="button"
                className={s.modalDangerBtn}
                onClick={handleConfirm}
                disabled={!dropReason.trim()}
              >
                <i className="fa fa-trash" /> Drop
              </button>
            </div>
          </>
        ) : variant === 'pricing' ? (
          <>
            <div className={s.modalHeader}>
              <i className="fa fa-line-chart" style={{ color: '#f97316' }} />
              {hasExistingPricing ? 'Update pricing snapshot' : 'Add pricing snapshot'}
            </div>
            <div className={s.modalBody}>
              <div className={s.modalLeadSummary}>
                <span className={s.modalLeadName}>{nameDisplay}</span>
                <span className={s.modalLeadPhone}>{phoneDisplay}</span>
                <span className={s.modalLeadStatus}>{lead.status}</span>
              </div>
              <p className={s.modalPrompt}>
                Record what the customer expected, what we quoted and the best transporter rate.
                Leave any field blank to clear it.
              </p>
              <PricingSnapshotFields value={pricing} onChange={setPricing} compact />
            </div>
            <div className={s.modalActions}>
              <button type="button" className={s.modalCancelBtn} onClick={onClose}>Cancel</button>
              <button type="button" className={s.modalConfirmBtn} onClick={handleConfirm}>
                <i className="fa fa-check" /> Save pricing
              </button>
            </div>
          </>
        ) : variant === 'pricing_convert' ? (
          <>
            <div className={s.modalHeader}>
              <i className="fa fa-line-chart" style={{ color: '#f97316' }} />
              Capture pricing before conversion
            </div>
            <div className={s.modalBody}>
              <div className={s.modalLeadSummary}>
                <span className={s.modalLeadName}>{nameDisplay}</span>
                <span className={s.modalLeadPhone}>{phoneDisplay}</span>
              </div>
              <p className={s.modalPrompt}>
                Record what the customer expected and the best quote available. Everything is optional.
              </p>
              <PricingSnapshotFields value={pricing} onChange={setPricing} compact />
            </div>
            <div className={s.modalActions}>
              <button type="button" className={s.modalCancelBtn} onClick={onClose}>Cancel</button>
              <button type="button" className={s.modalConfirmBtn} onClick={handleConfirm}>
                Continue to booking <i className="fa fa-arrow-right" />
              </button>
            </div>
          </>
        ) : variant === 'reset' ? (
          <>
            <div className={s.modalHeader}>
              <i className="fa fa-undo" />
              Reset to New
            </div>
            <div className={s.modalBody}>
              <div className={s.modalLeadSummary}>
                <span className={s.modalLeadName}>{nameDisplay}</span>
                <span className={s.modalLeadPhone}>{phoneDisplay}</span>
              </div>
              <p className={s.modalPrompt}>
                This lead will be reset to <strong>New</strong>. It will reappear in the New and Due today tabs.
              </p>
            </div>
            <div className={s.modalActions}>
              <button type="button" className={s.modalCancelBtn} onClick={onClose}>Cancel</button>
              <button type="button" className={s.modalConfirmBtn} onClick={handleConfirm}>
                <i className="fa fa-undo" /> Reset
              </button>
            </div>
          </>
        ) : variant === 'voice_call' ? (
          <>
            <div className={s.modalHeader}>
              <i className="fa fa-phone" style={{ color: '#3b82f6' }} />
              Call Customer
            </div>
            <div className={s.modalBody}>
              <div className={s.modalLeadSummary}>
                <span className={s.modalLeadName}>{nameDisplay}</span>
                <span className={s.modalLeadPhone}>{phoneDisplay}</span>
              </div>
              <p className={s.modalPrompt}>
                This will place an ops call to the customer via your device.
              </p>
              <CustomerDialNumberPicker
                options={dialOptions}
                value={dialPhone}
                onChange={setDialPhone}
              />
            </div>
            <div className={s.modalActions}>
              <button type="button" className={s.modalCancelBtn} onClick={onClose}>Cancel</button>
              <button type="button" className={s.modalConfirmBtn} onClick={() => onConfirm(dialPhone)}>
                <i className="fa fa-phone" /> Call
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default LeadStatusModal;
